import React, { useState, useEffect } from "react";
import apiService from "../services/apiService";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Tooltip,
  DialogActions,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  CssBaseline,
  Alert,
  TableContainer,
  Paper,
  Menu,
  Switch,
  MenuList,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import Autocomplete from "@mui/material/Autocomplete";
import { ReactComponent as StartTimeIcon } from "../Icons/Start time.svg";
import { ReactComponent as EndTimeIcon } from "../Icons/End time.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";
import { ReactComponent as EditIcon } from "../Icons/Edit.svg";
import { ReactComponent as NotesIcon } from "../Icons/Notes.svg";
import { ReactComponent as FailureReasonIcon } from "../Icons/Failure reason log detail.svg";
import { ReactComponent as CloneIcon } from "../Icons/Clone.svg";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { CircularProgress } from "@mui/material";
import {
  formatDateTimezoneFreeForFailureTracking,
  formatFailureTrackingDateTime,
  toFailureTrackingInputFormat,
  cleanFailureTrackingDate,
} from "../utils/failureTrackingDateUtils";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Dropdown options and mappings
const scheduledTypeOptions = [
  { value: "Listener Process", label: "Listener Process" },
  { value: "Scheduled Process", label: "Scheduled Process" },
  { value: "Sub Process", label: "Sub Process" },
];

const actionOptions = [
  { value: "EMAIL", label: "Email" },
  { value: "RERUN", label: "Rerun" },
  { value: "INCIDENT", label: "Incident" },
  { value: "SEV1", label: "SEV1" },
  { value: "NO_ACTION", label: "No Action Taken" },
];

const errorTypeOptions = [
  { value: "Data Error", label: "Data Error" },
  { value: "System Error", label: "System Error" },
];
const actionInPlaybookOptions = [
  "Email",
  "Email > 15 in 10 min",
  "Email > 50 in 10 min",
  "Incident",
  "NA",
  "No action",
  "Pager",
  "Rerun",
  "Sev1",
];

const emptyForm = {
  job_id: "",
  client_job_id: "",
  end_point: "",
  connector: "",
  folder: "",
  playbook_id: "",
  scheduled_type: "",
  process_type: "",
  reported_date: "",
  received_date: "",
  notes: "",
};

// Use the new utility function for cleaning dates
function cleanDateForInput(value) {
  return cleanFailureTrackingDate(value);
}
// Helper functions to map values to labels for table display
const getScheduledTypeLabel = (value) => {
  const option = scheduledTypeOptions.find((opt) => opt.value === value);
  return option ? option.label : value || "N/A";
};

const getActionLabel = (value) => {
  const option = actionOptions.find((opt) => opt.value === value);
  return option ? option.label : value || "N/A";
};

const getErrorTypeLabel = (value) => {
  const option = errorTypeOptions.find((opt) => opt.value === value);
  return option ? option.label : value || "N/A";
};

// Custom styles for reducing field height and width - matching standard app styling
const compactFieldStyles = {
  "& .MuiInputBase-root": {
    height: "36px",
    // Remove custom fontSize to use default
  },
  "& .MuiInputBase-input": {
    padding: "8px 10px",
    lineHeight: "1.2",
  },
  "& .MuiInputLabel-root": {
    // Remove custom fontSize to use default
    top: "-2px",
  },
  "& .MuiInputLabel-shrink": {
    top: "0",
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.75rem",
  },
};

const compactMultilineStyles = {
  "& .MuiInputBase-root": {
    minHeight: "80px",
    // Remove custom fontSize to use default
  },
  "& .MuiInputBase-input": {
    padding: "8px 10px",
    lineHeight: "1.2",
    overflowY: "auto",
  },
  "& .MuiInputLabel-root": {
    // Remove custom fontSize to use default
    top: "-2px",
  },
  "& .MuiInputLabel-shrink": {
    top: "0",
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.75rem",
  },
};
const HELPER_HEIGHT = 20;

// Consistent dropdown menu styling - matching Application page standards
const dropdownMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 200,
      width: "auto",
    },
  },
};

// Standard menu item styling - matching Application page
const menuItemStyles = {
  fontSize: "0.875rem",
  minHeight: "32px",
  padding: "4px 8px",
};

function FailureTrackingPopup({
  open,
  onClose,
  jobDetails: propJobDetails,
  je_id,
  jobError,
  onRefresh,
  jobScript,
}) {
  // helper to build initial form data using props
  const buildInitialFormData = () => ({
    cl_id: jobError?.cl_id || "N/A",
    client_name: jobError?.client_name || "N/A",
    env_id: jobError?.env_id || "N/A",
    env_name: jobError?.env_name || "N/A",
    app_id: jobError?.app_id || "N/A",
    app_name: jobError?.app_name || "N/A",
    je_id: je_id || "N/A",
    jed_id: "",
    job_id: jobError?.job_id || "N/A",
    client_job_id: jobError?.client_job_id || "N/A",
    reported_date: jobError?.reported_date || "N/A",
    received_date: jobError?.received_date || "N/A",
    // reported_date: "",
    // received_date: "",
    time_zone: "",
    error_time: "",
    error_message: "",
    criticality: "",
    playbook_flag: "",
    playbook_id: "",
    action_in_playbook: "",
    occurrence: "",
    process_reporting_url: "",
    folder: "",
    analysis: "",
    scheduled_type: jobError?.process_type,
    error_type: "",
    error_name: "",
    end_point: "",
    connector: "",
    action: "",
    doc_id: "",
    comments: "",
    active_flag: "1",
  });

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});

  // Check if form has changed
  const hasFormChanged = () => {
    if (!isEditing) return true;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const [fetchedJobError, setFetchedJobError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [editField, setEditField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localJobDetails, setLocalJobDetails] = useState([]);
  const [playbookIdOptionsDynamic, setPlaybookIdOptionsDynamic] = useState([]);
  const [connectorOptionsDynamic, setConnectorOptionsDynamic] = useState([]);
  const [toggling, setToggling] = useState(false);
  const [endpointOptionsDynamic, setEndpointOptionsDynamic] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [editSuccess, setEditSuccess] = useState("");
  const [editError, setEditError] = useState("");
  const [folderOptions, setFolderOptions] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteJedId, setDeleteJedId] = useState(null);
  const [confirmClone, setConfirmClone] = useState({
    open: false,
    jed_id: null,
  });
  // Fetch folders from backend when component mounts
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const foldersData = await apiService.get("/job_errors/folders-list");

        const folderOptionsFormatted = Array.isArray(foldersData)
          ? foldersData.map((folder) => ({ value: folder, label: folder }))
          : [];
        setFolderOptions(folderOptionsFormatted);
      } catch (error) {
        console.error("Error fetching folders:", error);
        setFolderOptions([]);
      }
    };

    fetchFolders();
  }, []);
  const parentReportedDate =
    fetchedJobError?.reported_date || jobError?.reported_date
      ? toFailureTrackingInputFormat(
          fetchedJobError?.reported_date || jobError?.reported_date,
        )
      : "";
  const parentReceivedDate =
    fetchedJobError?.received_date || jobError?.received_date
      ? toFailureTrackingInputFormat(
          fetchedJobError?.received_date || jobError?.received_date,
        )
      : "";
  //-------------------------------------
  const [fieldPopupOpen, setFieldPopupOpen] = useState(false);
  const [fieldName, setFieldName] = useState("");
  const [fieldValue, setFieldValue] = useState("");
  const [labelWidth, setLabelWidth] = useState("150px");

  const [formData, setFormData] = useState(buildInitialFormData());
  const [formErrors, setFormErrors] = useState({});

  const hasCompleteJobError =
    jobError &&
    jobError.cl_id &&
    jobError.env_id &&
    jobError.app_id &&
    jobError.job_id;

  const jobDetails =
    propJobDetails && propJobDetails.length > 0
      ? propJobDetails
      : localJobDetails;

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    client_name: true,
    env_name: true,
    app_name: true,
    je_id: true,
    jed_id: true,
    job_id: true,
    client_job_id: true,
    reported_date: true,
    received_date: true,
    time_zone: true,
    error_time: true,
    error_message: true,
    criticality: true,
    playbook_flag: true,
    playbook_id: true,
    action_in_playbook: true,
    occurrence: true,
    process_reporting_url: true,
    folder: true,
    analysis: true,
    scheduled_type: true,
    error_type: true,
    error_name: true,
    end_point: true,
    connector: true,
    action: true,
    doc_id: true,
    comments: true,
    active_flag: true,
  });

  // Column labels for display in the menu
  const columnLabels = {
    client_name: "Client",
    env_name: "Environment",
    app_name: "Application",
    je_id: "Failure Tracking ID",
    jed_id: "Failure Tracking Detail ID",
    job_id: "Job ID",
    client_job_id: "Client Job ID",
    reported_date: "Reported Date",
    received_date: "Received Date",
    time_zone: "Time Zone",
    error_time: "Error Time",
    error_message: "Error Message",
    criticality: "Criticality",
    playbook_flag: "Playbook Flag",
    playbook_id: "Playbook ID",
    action_in_playbook: "Action In Playbook",
    occurrence: "Occurrence",
    process_reporting_url: "Process Reporting URL",
    folder: "Folder",
    analysis: "Analysis",
    scheduled_type: "Scheduled Type",
    error_type: "Error Type",
    error_name: "Error Name",
    end_point: "End Point",
    connector: "Connector",
    action: "Actions",
    doc_id: "Doc ID",
    comments: "Comments",
    active_flag: "Active Flag",
  };

  // Handlers for column filter menu
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Fetch related data when job_id changes
  useEffect(() => {
    const fetchRelatedData = async () => {
      if (formData.job_id && formData.job_id !== "N/A") {
        try {
          // Fetch playbook IDs
          const playbookData = await apiService.get(
            `/job_error_details/playbooks/${formData.job_id}`,
          );

          let playbookOptions = Array.isArray(playbookData)
            ? playbookData.map((item) => ({
                value: item.playbook_id,
                label: item.playbook_id,
              }))
            : [];
          if (
            formData.playbook_id &&
            !playbookOptions.find((opt) => opt.value === formData.playbook_id)
          ) {
            playbookOptions.push({
              value: formData.playbook_id,
              label: formData.playbook_id,
            });
          }
          setPlaybookIdOptionsDynamic(
            playbookOptions.length > 0
              ? playbookOptions
              : [{ value: "", label: "No Playbooks Available" }],
          );

          // Fetch connectors
          const connectorData = await apiService.get(
            `/job_error_details/connector/${formData.job_id}`,
          );

          let connectorOptions = Array.isArray(connectorData)
            ? [...new Set(connectorData.map((item) => item.connector))]
                .filter((connector) => connector)
                .map((connector) => ({
                  value: connector,
                  label: connector,
                }))
            : [];
          if (
            formData.connector &&
            !connectorOptions.find((opt) => opt.value === formData.connector)
          ) {
            connectorOptions.push({
              value: formData.connector,
              label: formData.connector,
            });
          }
          setConnectorOptionsDynamic(
            connectorOptions.length > 0
              ? connectorOptions
              : [{ value: "", label: "No Connectors Available" }],
          );

          // Fetch endpoints
          const endpointData = await apiService.get(
            `/job_error_details/endpoints/${formData.job_id}`,
          );

          let endpointOptions = Array.isArray(endpointData)
            ? [...new Set(endpointData.map((item) => item.end_point))]
                .filter((end_point) => end_point)
                .map((end_point) => ({
                  value: end_point,
                  label: end_point,
                }))
            : [];
          if (
            formData.end_point &&
            !endpointOptions.find((opt) => opt.value === formData.end_point)
          ) {
            endpointOptions.push({
              value: formData.end_point,
              label: formData.end_point,
            });
          }
          setEndpointOptionsDynamic(
            endpointOptions.length > 0
              ? endpointOptions
              : [{ value: "", label: "No Endpoints Available" }],
          );
          if (!formData.end_point && endpointOptions.length > 0) {
            setFormData((prev) => ({
              ...prev,
              end_point: endpointOptions[0].value,
            }));
          }
        } catch (error) {
          console.error("Error fetching related data:", error);
          setApiError("Failed to fetch related data for job ID.");
          const playbookOptions = formData.playbook_id
            ? [{ value: formData.playbook_id, label: formData.playbook_id }]
            : [{ value: "", label: "No Playbooks Available" }];
          const connectorOptions = formData.connector
            ? [{ value: formData.connector, label: formData.connector }]
            : [{ value: "", label: "No Connectors Available" }];
          const endpointOptions = formData.end_point
            ? [{ value: formData.end_point, label: formData.end_point }]
            : [{ value: "", label: "No Endpoints Available" }];
          setPlaybookIdOptionsDynamic(playbookOptions);
          setConnectorOptionsDynamic(connectorOptions);
          setEndpointOptionsDynamic(endpointOptions);
        }
      } else {
        const playbookOptions = formData.playbook_id
          ? [{ value: formData.playbook_id, label: formData.playbook_id }]
          : [{ value: "", label: "No Playbooks Available" }];
        const connectorOptions = formData.connector
          ? [{ value: formData.connector, label: formData.connector }]
          : [{ value: "", label: "No Connectors Available" }];
        const endpointOptions = formData.end_point
          ? [{ value: formData.end_point, label: formData.end_point }]
          : [{ value: "", label: "No Endpoints Available" }];
        setPlaybookIdOptionsDynamic(playbookOptions);
        setConnectorOptionsDynamic(connectorOptions);
        setEndpointOptionsDynamic(endpointOptions);
      }
    };

    fetchRelatedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.job_id,
    formData.playbook_id,
    formData.connector,
    formData.end_point,
    formData.scheduled_type,
  ]);

  useEffect(() => {
    const fetchJobErrorDetails = async () => {
      if (hasCompleteJobError) {
        setFormData((prev) => ({
          ...prev,
          cl_id: jobError.cl_id,
          client_name: jobError.client_name || "N/A",
          env_id: jobError.env_id,
          env_name: jobError.env_name || "N/A",
          app_id: jobError.app_id,
          app_name: jobError.app_name || "N/A",
          je_id: je_id || "N/A",
          job_id: jobError.job_id,
          client_job_id: jobError.client_job_id || "",
          scheduled_type: jobError.process_type || "",
          reported_date: jobError.reference_date_1
            ? toFailureTrackingInputFormat(jobError.reference_date_1)
            : "",
          received_date: jobError.received_date
            ? toFailureTrackingInputFormat(jobError.received_date)
            : "",
        }));
        return;
      }

      if (je_id && je_id !== "N/A") {
        try {
          const jobErrorData = await apiService.get(`/job_errors/${je_id}`);

          setFetchedJobError(jobErrorData);
          setApiError(null);
          setFormData((prev) => ({
            ...prev,
            cl_id: jobErrorData?.cl_id || jobError?.cl_id || "N/A",
            client_name:
              jobErrorData?.client_name || jobError?.client_name || "N/A",
            env_id: jobErrorData?.env_id || jobError?.env_id || "N/A",
            env_name: jobErrorData?.env_name || jobError?.env_name || "N/A",
            app_id: jobErrorData?.app_id || jobError?.app_id || "N/A",
            app_name: jobErrorData?.app_name || jobError?.app_name || "N/A",
            je_id: je_id || "N/A",
            job_id: jobErrorData?.job_id || jobError?.job_id || "N/A",
            client_job_id:
              jobErrorData?.client_job_id || jobError?.client_job_id || "",
            scheduled_type: jobErrorData?.process_type || "",
            reported_date: jobErrorData?.reference_date_1
              ? toFailureTrackingInputFormat(jobErrorData.reference_date_1)
              : jobError?.reference_date_1
                ? toFailureTrackingInputFormat(jobError.reference_date_1)
                : "",
            received_date: jobErrorData?.received_date
              ? toFailureTrackingInputFormat(jobErrorData.received_date)
              : jobError?.received_date
                ? toFailureTrackingInputFormat(jobError.received_date)
                : "",
          }));
        } catch (error) {
          console.error("Error fetching job_error details:", error);
          setFetchedJobError(null);
          setApiError("Failed to fetch job error details from the server.");
          setFormData((prev) => ({
            ...prev,
            cl_id: jobError?.cl_id || "N/A",
            client_name: jobError?.client_name || "N/A",
            env_id: jobError?.env_id || "N/A",
            env_name: jobError?.env_name || "N/A",
            app_id: jobError?.app_id || "N/A",
            app_name: jobError?.app_name || "N/A",
            je_id: je_id || "N/A",
            job_id: jobError?.job_id || "N/A",
            client_job_id: jobError?.client_job_id || "",
            scheduled_type: jobError?.process_type || "",
            reported_date: jobError?.reference_date_1
              ? toFailureTrackingInputFormat(jobError.reference_date_1)
              : "",
            received_date: jobError?.received_date
              ? toFailureTrackingInputFormat(jobError.received_date)
              : "",
          }));
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          cl_id: jobError?.cl_id || "N/A",
          client_name: jobError?.client_name || "N/A",
          env_id: jobError?.env_id || "N/A",
          env_name: jobError?.env_name || "N/A",
          app_id: jobError?.app_id || "N/A",
          app_name: jobError?.app_name || "N/A",
          je_id: je_id || "N/A",
          job_id: jobError?.job_id || "N/A",
          client_job_id: jobError?.client_job_id || "",
          scheduled_type: jobError?.process_type || "",
          reported_date: jobError?.reference_date_1
            ? toFailureTrackingInputFormat(jobError.reference_date_1)
            : "",
          received_date: jobError?.received_date
            ? toFailureTrackingInputFormat(jobError.received_date)
            : "",
        }));
      }
    };

    fetchJobErrorDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [je_id, jobError, hasCompleteJobError]);

  useEffect(() => {
    const fetchJobErrorDetailsList = async () => {
      if (propJobDetails && propJobDetails.length > 0) {
        setLocalJobDetails([]);
        setInitialLoading(false);
        return;
      }

      if (je_id && je_id !== "N/A") {
        try {
          setLoading(true);
          setInitialLoading(true);
          const details = await apiService.get(`/job_error_details/${je_id}`);

          setLocalJobDetails(
            Array.isArray(details)
              ? details.map((item) => ({
                  ...item,
                  active_flag: item.active_flag.toString(),
                }))
              : [],
          );
          setApiError(null);
        } catch (error) {
          console.error("Error fetching job_error_details:", error);
          setLocalJobDetails([]);
          setApiError(
            error.message ||
              "Failed to fetch job error details from the server.",
          );
        } finally {
          setLoading(false);
          setInitialLoading(false);
        }
      } else {
        setInitialLoading(false);
      }
    };

    fetchJobErrorDetailsList();
  }, [je_id, propJobDetails]);

  function toDbFormat(inputValue) {
    if (!inputValue) return null;
    if (inputValue.includes("T")) {
      return inputValue.replace("T", " ") + ":00.000";
    }
    return inputValue;
  }
  const formatDateForInput = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toISOString().slice(0, 10);
    } catch {
      return value;
    }
  };
  // Use the new utility function for input formatting
  function toInputFormat(value, type = "date") {
    if (type === "date") {
      return toFailureTrackingInputFormat(value);
    }

    if (!value) return "";

    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return "";

      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");

      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    } catch {
      return "";
    }
  }
  // Use the new timezone-free date formatting utility

  // Use the new utility function for datetime display

  const handleAddClick = async () => {
    // ← make it async
    try {
      // 1. Calculate next jed_id (your existing logic)
      const maxJedId =
        jobDetails.length > 0
          ? Math.max(...jobDetails.map((d) => parseInt(d.jed_id, 10)), 0) + 1
          : 1;

      // 2. Prepare base form data
      let newFormData = {
        ...emptyForm,
        cl_id: fetchedJobError?.cl_id || jobError?.cl_id || "",
        client_name:
          fetchedJobError?.client_name || jobError?.client_name || "",
        env_id: fetchedJobError?.env_id || jobError?.env_id || "",
        env_name: fetchedJobError?.env_name || jobError?.env_name || "",
        app_id: fetchedJobError?.app_id || jobError?.app_id || "",
        app_name: fetchedJobError?.app_name || jobError?.app_name || "",
        je_id: je_id || "",
        jed_id: String(maxJedId),
        job_id: fetchedJobError?.job_id || jobError?.job_id || "",
        client_job_id:
          (fetchedJobError?.app_name || jobError?.app_name) === "GARPAC"
            ? "-"
            : fetchedJobError?.client_job_id || jobError?.client_job_id || "-",
        reported_date:
          fetchedJobError?.reference_date_1 || jobError?.reference_date_1
            ? toFailureTrackingInputFormat(
                fetchedJobError?.reference_date_1 || jobError?.reference_date_1,
              )
            : "",
        received_date:
          fetchedJobError?.received_date || jobError?.received_date
            ? toFailureTrackingInputFormat(
                fetchedJobError?.received_date || jobError?.received_date,
              )
            : "",
        time_zone:
          (fetchedJobError?.app_name || jobError?.app_name) === "Boomi"
            ? "PST"
            : "",
        scheduled_type:
          fetchedJobError?.process_type || jobError?.process_type || "",
      };

      // 3. Fetch folder automatically if job_id exists
      if (newFormData.job_id && newFormData.job_id !== "N/A") {
        try {
          setFolderLoading(true);
          const folderResponse = await apiService.get(
            `/job_error_details/folder/${newFormData.job_id}`,
          );

          if (folderResponse && folderResponse.folder) {
            newFormData.folder = folderResponse.folder;
          } else {
            newFormData.folder = ""; // or "-" or null
          }
        } catch (err) {
          console.warn("Could not fetch folder → leaving empty", err);
          newFormData.folder = "";
        } finally {
          setFolderLoading(false);
        }
      }

      // 4. Apply the prepared data
      setFormData(newFormData);

      setIsEditing(false);
      setShowForm(true);
      setFormErrors({});
      setApiError(null);
      // onRefresh();  // ← usually not needed on add click
    } catch (err) {
      console.error("Error preparing add form:", err);
      setApiError("Failed to prepare form");
    }
  };
  const handleEditClick = (detail) => {
    const mappedPlaybookFlag =
      detail.playbook_flag === "YES"
        ? "Y"
        : detail.playbook_flag === "NO"
          ? "N"
          : detail.playbook_flag || "";

    const mappedCriticality =
      detail.criticality === "LOW"
        ? "L"
        : detail.criticality === "MEDIUM"
          ? "M"
          : detail.criticality === "HIGH"
            ? "H"
            : detail.criticality || "";

    setFormData({
      // ...emptyForm,
      ...detail,

      // Correct date mapping with input formatting
      reported_date: detail.reported_date
        ? toFailureTrackingInputFormat(detail.reported_date)
        : fetchedJobError?.reference_date_1
          ? toFailureTrackingInputFormat(fetchedJobError.reference_date_1)
          : "",

      received_date: detail.received_date
        ? toFailureTrackingInputFormat(detail.received_date)
        : fetchedJobError?.received_date
          ? toFailureTrackingInputFormat(fetchedJobError.received_date)
          : "",

      // Ensures dropdown values show correctly
      playbook_id: detail.playbook_id || "",
      playbook_flag: mappedPlaybookFlag || "",
      connector: detail.connector || "",
      end_point: detail.end_point || "",

      // Correct criticality mapping
      criticality: mappedCriticality,

      // Fix for Boomi auto timezone
      time_zone: detail.app_name === "Boomi" ? "PST" : detail.time_zone || "",

      // Do NOT reset process_type when editing
      scheduled_type: detail.scheduled_type || detail.process_type || "",
    });

    setIsEditing(true);
    setShowForm(true);
    setFormErrors({});
    setApiError(null);
    onRefresh();
  };

  // Store original form data after formData is set during edit
  useEffect(() => {
    if (isEditing && showForm && Object.keys(formData).length > 0) {
      setOriginalFormData({ ...formData });
    }
  }, [isEditing, showForm]); // Only run when entering edit mode
  const handleDialogClose = () => {
    setShowForm(false);
    setIsEditing(false);
    // setFormData(buildInitialFormData());
    setFormData((prev) => ({
      ...prev,
      reported_date: "",
      received_date: "",
    }));
    setFormErrors({});
    setApiError(null);
    onRefresh();
  };

  const handleDeleteClick = (jed_id) => {
    setDeleteJedId(jed_id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.delete(`/job_error_details/${deleteJedId}`);

      setSuccessMessage("Job error detail deleted successfully!");
      setOpenDeleteDialog(false);
      setDeleteJedId(null);

      onRefresh();
      setTimeout(() => setSuccessMessage(""), 3000);

      if (!propJobDetails || propJobDetails.length === 0) {
        const data = await apiService.get(`/job_error_details/${je_id}`);
        setLocalJobDetails(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Delete failed:", error);
      setApiError("Failed to delete record");
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteJedId(null);
  };
  const handleDeleteDetail = async (jed_id) => {
    if (
      window.confirm("Are you sure you want to delete this job error detail?")
    ) {
      try {
        await apiService.delete(`/job_errors/details/${jed_id}`);
        setSuccessMessage("Job error detail deleted successfully!");
        onRefresh();
        setTimeout(() => setSuccessMessage(""), 3000);
        if (!propJobDetails || propJobDetails.length === 0) {
          const data = await apiService.get(`/job_error_details/${je_id}`);
          setLocalJobDetails(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error deleting job error detail:", error);
        alert(`Delete failed: ${error.message}`);
      }
    }
  };

  const handleCloneClick = (jed_id) => {
    setConfirmClone({ open: true, jed_id });
  };

  const performClone = async (jed_id) => {
    try {
      await apiService.post(`/job_error_details/clone/${jed_id}`);
      setSuccessMessage("Job error detail cloned successfully!");
      onRefresh();
      setTimeout(() => setSuccessMessage(""), 3000);
      if (!propJobDetails || propJobDetails.length === 0) {
        const data = await apiService.get(`/job_error_details/${je_id}`);
        setLocalJobDetails(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error cloning job error detail:", error);
      setApiError(`Clone failed: ${error.message}`);
      setTimeout(() => setApiError(""), 5000);
    }
  };

  const handleToggleFlag = async (jed_id, currentFlag) => {
    setToggling(true);
    const newFlag = currentFlag === 1 ? 0 : 1;

    const updatedDetails = (propJobDetails || localJobDetails).map((detail) =>
      detail.jed_id === jed_id
        ? { ...detail, active_flag: newFlag.toString() }
        : detail,
    );
    setLocalJobDetails(updatedDetails);

    try {
      const responseData = await apiService.patch(
        `/job_error_details/toggle/${jed_id}`,
        { active_flag: newFlag },
      );

      setSuccessMessage(
        newFlag === 1
          ? "Successfully set to Active!"
          : "Successfully set to Inactive!",
      );
      setApiError(null);
      onRefresh();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      setApiError(error.message);
      setLocalJobDetails(propJobDetails || localJobDetails);
    } finally {
      setToggling(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let updatedFormData = { ...prev, [name]: value };

      if (name === "error_time") {
        const times = value
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0);
        updatedFormData.occurrence = times.length.toString();
      }

      if (name === "job_id") {
        updatedFormData.playbook_id = "";
        updatedFormData.connector = "";
        updatedFormData.end_point = "";
      }

      return updatedFormData;
    });
  };
  const validateForm = () => {
    const errors = {};

    if (formData.cl_id === "N/A" || !formData.cl_id)
      errors.cl_id = "Client ID is required.";
    if (formData.env_id === "N/A" || !formData.env_id)
      errors.env_id = "Environment ID is required.";
    if (formData.app_id === "N/A" || !formData.app_id)
      errors.app_id = "Application ID is required.";
    if (formData.je_id === "N/A" || !formData.je_id)
      errors.je_id = "Job Error ID is required.";
    if (formData.job_id === "N/A" || !formData.job_id)
      errors.job_id = "Job ID is required.";
    if (!formData.reported_date || formData.reported_date === "N/A")
      errors.reported_date = "Reported Date is required.";
    if (!formData.received_date || formData.received_date === "N/A")
      errors.received_date = "Received Date is required.";
    if (!formData.error_time) errors.error_time = "Error Time is required.";
    if (!formData.action) errors.action = "Action is required.";
    if (!formData.error_message)
      errors.error_message = "Error Message is required.";
    if (!formData.criticality) errors.criticality = "Criticality is required.";
    if (!formData.error_type) errors.error_type = "Error Type is required.";
    if (!formData.analysis) errors.analysis = "Analysis is required.";
    const isBoomiApp = formData.app_name === "Boomi";
    if (isBoomiApp && !formData.client_job_id) {
      errors.client_job_id = "Client Job ID is required for Boomi.";
    }

    return errors;
  };

  const handleFormSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      const errorMsg =
        "Please fill in all required fields: " +
        Object.values(errors).join(" ");
      setApiError(errorMsg);
      setTimeout(() => setApiError(""), 3000);
      return;
    }

    const payload = {
      cl_id: formData.cl_id,
      env_id: formData.env_id,
      app_id: formData.app_id,
      je_id: formData.je_id,
      jed_id: formData.jed_id,
      job_id: formData.job_id,
      client_job_id:
        formData.app_name === "GARPAC" && !formData.client_job_id
          ? "-"
          : formData.client_job_id === null
            ? "-"
            : formData.client_job_id || "-",
      reported_date: formData.reported_date,
      received_date: formData.received_date,
      time_zone:
        formData.app_name === "Boomi" ? "PST" : formData.time_zone || null,
      error_time: formData.error_time || null,
      error_message: formData.error_message,
      criticality: formData.criticality,
      playbook_flag: formData.playbook_flag || null,
      playbook_id: formData.playbook_id || null,
      action_in_playbook: formData.action_in_playbook || null,
      occurrence: formData.occurrence ? parseInt(formData.occurrence) : null,
      process_reporting_url: formData.process_reporting_url || null,
      folder: formData.folder || null,
      analysis: formData.analysis || null,
      scheduled_type: formData.scheduled_type || null,
      error_type: formData.error_type,
      error_name: formData.error_name?.trim() || "N/A",
      end_point: formData.end_point || null,
      connector: formData.connector || null,
      action: formData.action || null,
      doc_id: formData.doc_id || null,
      comments: formData.comments || null,
      active_flag: formData.active_flag ? parseInt(formData.active_flag) : 1,
    };

    try {
      await apiService[isEditing ? "put" : "post"](
        isEditing
          ? `/job_error_details/${formData.jed_id}`
          : `/job_error_details`,
        payload,
      );

      setSuccessMessage(
        isEditing
          ? "Job error detail updated successfully!"
          : "Job error detail added successfully!",
      );
      setShowForm(false);
      onRefresh();
      setTimeout(() => setSuccessMessage(""), 3000);
      if (!propJobDetails || propJobDetails.length === 0) {
        const data = await apiService.get(`/job_error_details/${je_id}`);
        setLocalJobDetails(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error submitting job error detail:", error);
      setApiError(error.message);
      setTimeout(() => setApiError(""), 3000);
    }
  };

  const updateSingleField = async (jed_id, field, value) => {
    try {
      await apiService.patch(`/job_error_details/${jed_id}/field`, {
        field,
        value: field === "reported_date" ? toDbFormat(value) : value,
      });

      onRefresh();
      if (!propJobDetails || propJobDetails.length === 0) {
        const data = await apiService.get(`/job_error_details/${je_id}`);
        setLocalJobDetails(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error updating field:", err);
      throw err;
    }
  };

  const columns = [
    "client_name",
    "env_name",
    "app_name",
    "je_id",
    "jed_id",
    "job_id",
    "client_job_id",
    "reported_date",
    "received_date",
    "time_zone",
    "error_time",
    "error_message",
    "criticality",
    "playbook_flag",
    "playbook_id",
    "action_in_playbook",
    "occurrence",
    "process_reporting_url",
    "folder",
    "analysis",
    "scheduled_type",
    "error_type",
    "error_name",
    "end_point",
    "connector",
    "action",
    "doc_id",
    "comments",
    "active_flag",
  ];
  const TruncatedCell = ({ content, maxWidth = "300px", columnKey }) => {
    const [open, setOpen] = useState(false);

    // Empty / null / N/A
    if (!content || content.toString().trim() === "" || content === "N/A") {
      return (
        <TableCell align="left" sx={{ color: "#999" }}>
          -
        </TableCell>
      );
    }

    const displayText = content.toString();
    const shouldTruncate = displayText.length > 40;

    // Get the exact column name from your columnLabels object
    const columnName = columnLabels[columnKey] || columnKey; // e.g., "Error Message"

    return (
      <>
        <TableCell
          align="left"
          onClick={() => shouldTruncate && setOpen(true)}
          sx={{
            maxWidth,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: shouldTruncate ? "pointer" : "default",
            "&:hover": { bgcolor: "action.hover" },
          }}
          title={shouldTruncate ? displayText : undefined}
        >
          {displayText}
          {shouldTruncate && (
            <span style={{ color: "#666", marginLeft: 4 }}>...</span>
          )}
        </TableCell>

        {/* Popup – shows only the real column name */}
        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpen(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold", pr: 8 }}>
            {columnName}
            <IconButton
              onClick={() => setOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                p: 3,
                bgcolor: "#f9f9f9",
                borderRadius: 2,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "1rem",
                lineHeight: 1.7,
              }}
            >
              {displayText}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpen(false)}
              variant="contained"
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {openDeleteDialog && (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1400,
              bgcolor: "white",
              borderRadius: "10px",
              // border: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Confirm Delete
            </DialogTitle>

            <Typography fontSize="0.9rem">
              Are you sure you want to delete this Failure{" "}
              <strong>{deleteJedId}</strong> (Job ID:{" "}
              <strong>
                {(propJobDetails || localJobDetails).find(
                  (d) => d.jed_id === deleteJedId,
                )?.job_id || "N/A"}
              </strong>
              )?
            </Typography>

            <Box
              sx={{ p: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}
            >
              <Button
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setDeleteJedId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    await apiService.delete(
                      `/job_error_details/${deleteJedId}`,
                    );

                    setSuccessMessage("Job error detail deleted successfully!");
                    onRefresh();

                    if (!propJobDetails || propJobDetails.length === 0) {
                      const data = await apiService.get(
                        `/job_error_details/${je_id}`,
                      );
                      setLocalJobDetails(Array.isArray(data) ? data : []);
                    }
                  } catch (error) {
                    console.error("Delete failed:", error);
                    setApiError("Failed to delete record");
                  } finally {
                    setOpenDeleteDialog(false);
                    setDeleteJedId(null);
                  }
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        )}

        {/* Confirm Clone - Simple overlay without Dialog */}

        {confirmClone.open && (
          <Box
            sx={{
              position: "fixed",
              top: "50%", // same as delete
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1400,
              bgcolor: "white",
              borderRadius: "10px",
              padding: 2,
              minWidth: 350,
            }}
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Clone</DialogTitle>

            <Typography fontSize="0.9rem">
              Are you sure you want to clone this Failure{" "}
              <strong>{confirmClone.jed_id}</strong> ?
            </Typography>

            <Box
              sx={{
                p: 1,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                onClick={() => setConfirmClone({ open: false, jed_id: null })}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    if (confirmClone.jed_id) {
                      await performClone(confirmClone.jed_id);
                      setSuccessMessage(
                        "Job error detail cloned successfully!",
                      );
                      onRefresh();
                      setTimeout(() => setSuccessMessage(""), 3000);
                    }
                  } catch (err) {
                    setApiError(
                      "Failed to clone: " + (err.message || "Unknown error"),
                    );
                    setTimeout(() => setApiError(""), 5000);
                  } finally {
                    setConfirmClone({ open: false, jed_id: null });
                  }
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        )}
      </>
    );
  };
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* MAIN FAILURE TRACKING POPUP (keeps table + controls) */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          onClose();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Failure Tracking Details of : {jobScript || jobError?.jobs_script || "N/A"}
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ mt: 1 }} />
        <Box
          sx={{
            ml: 1.5,
            mb: -1.5,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 0,
            px: 2,
            py: 1.5,
            mt: -1,
          }}
        >
          <Button
            onClick={handleOpen}
            sx={{
              color: "#666",
              height: "35px",
              width: "10px",
              borderRadius: "8px",
              "&:hover": {
                color: "#1976d2",
              },
            }}
          >
            <Tooltip title="Column Customization" arrow>
              <SettingsIcon style={{ width: 17, height: 17 }} />
            </Tooltip>
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 200,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            <MenuList>
              {Object.keys(visibleColumns).map((col) => (
                <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      border: "1px solid #ccc",
                      borderRadius: 1.5,
                      px: 2,
                      py: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: "#333",
                        fontWeight: "Bold",
                      }}
                    >
                      {columnLabels[col] || col}
                    </Typography>
                    <Switch
                      size="small"
                      checked={visibleColumns[col]}
                      onChange={(e) =>
                        setVisibleColumns((prev) => ({
                          ...prev,
                          [col]: e.target.checked,
                        }))
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2DB0E4",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#2DB0E4",
                          },
                      }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          <Button
            onClick={handleAddClick}
            sx={{
              color: "#666",
              fontWeight: "bold",
              borderRadius: "8px",
              height: "35px",
              minWidth: "50px",

              textTransform: "none",
              fontSize: "12px",
              "&:hover": {
                color: "#1976d2",
              },
            }}
          >
            Add +
          </Button>
        </Box>
        <DialogContent>
          {successMessage && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {successMessage}
            </Alert>
          )}
          {apiError && (
            <Alert severity="error" sx={{ mb: 0 }}>
              {apiError}
            </Alert>
          )}

          {initialLoading ? (
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              minHeight="200px"
              sx={{
                padding: "20px",
              }}
            >
              <Typography variant="body1">
                Loading failure tracking details...
              </Typography>
            </Box>
          ) : jobDetails.length > 0 ? (
            <ThemeProvider theme={Table_theme}>
              <Paper elevation={3}>
                <TableContainer sx={{ maxHeight: "70vh" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{}}>
                        <TableCell>Actions</TableCell>
                        {columns.map(
                          (col) =>
                            visibleColumns[col] && (
                              <TableCell
                                key={col}
                                align="left"
                                sx={{
                                  fontWeight: "bold",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {columnLabels[col]}
                              </TableCell>
                            ),
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobDetails.map((detail, index) => (
                        <TableRow key={`${detail.jed_id}_${index}`} hover>
                          {/* Sticky Actions */}
                          <TableCell
                            sx={{
                              left: 0,
                              bgcolor: "background.paper",
                              zIndex: 9,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Box display="flex" gap={0.5}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setEditField({
                                    jed_id: detail.jed_id,
                                    field: "reported_date",
                                    value: toFailureTrackingInputFormat(
                                      detail.reported_date,
                                    ),
                                  })
                                }
                              >
                                <StartTimeIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setEditField({
                                    jed_id: detail.jed_id,
                                    field: "error_time",
                                    value: detail.error_time,
                                  })
                                }
                              >
                                <EndTimeIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setEditField({
                                    jed_id: detail.jed_id,
                                    field: "error_message",
                                    value: detail.error_message,
                                  })
                                }
                              >
                                <FailureReasonIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setEditField({
                                    jed_id: detail.jed_id,
                                    field: "comments",
                                    value: detail.comments,
                                  })
                                }
                              >
                                <NotesIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleCloneClick(detail.jed_id)}
                              >
                                <CloneIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(detail.jed_id)}
                              >
                                <DeleteIcon sx={{ color: "error.main" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(detail)}
                              >
                                <EditIcon sx={{ color: "primary.main" }} />
                              </IconButton>
                              {/* <IconButton size="small" onClick={() => handleToggleFlag(detail.jed_id, parseInt(detail.active_flag))}>
                                <FlagIcon style={{ color: detail.active_flag === "1" ? "#00CC22" : "#00CC22" }} />
                              </IconButton> */}
                            </Box>
                          </TableCell>

                          {/* All Columns Using TruncatedCell */}
                          {visibleColumns.client_name && (
                            <TruncatedCell content={detail.client_name} />
                          )}
                          {visibleColumns.env_name && (
                            <TruncatedCell content={detail.env_name} />
                          )}
                          {visibleColumns.app_name && (
                            <TruncatedCell content={detail.app_name} />
                          )}
                          {visibleColumns.je_id && (
                            <TruncatedCell content={detail.je_id} />
                          )}
                          {visibleColumns.jed_id && (
                            <TruncatedCell content={detail.jed_id} />
                          )}
                          {visibleColumns.job_id && (
                            <TruncatedCell content={detail.job_id} />
                          )}
                          {visibleColumns.client_job_id && (
                            <TruncatedCell
                              content={detail.client_job_id || "-"}
                            />
                          )}
                          {visibleColumns.reported_date && (
                            <TruncatedCell
                              content={formatDateTimezoneFreeForFailureTracking(
                                detail.reported_date,
                              )}
                            />
                          )}
                          {visibleColumns.received_date && (
                            <TruncatedCell
                              content={formatDateTimezoneFreeForFailureTracking(
                                detail.received_date,
                              )}
                            />
                          )}
                          {visibleColumns.time_zone && (
                            <TruncatedCell content={detail.time_zone || "-"} />
                          )}
                          {visibleColumns.error_time && (
                            <TruncatedCell
                              content={detail.error_time || "-"}
                              maxWidth="400px"
                              columnKey="error_time"
                            />
                          )}
                          {visibleColumns.error_message && (
                            <TruncatedCell
                              content={detail.error_message || "-"}
                              maxWidth="500px"
                              columnKey="error_message"
                            />
                          )}
                          {visibleColumns.criticality && (
                            <TruncatedCell
                              content={
                                detail.criticality === "L"
                                  ? "Low"
                                  : detail.criticality === "M"
                                    ? "Medium"
                                    : detail.criticality === "H"
                                      ? "High"
                                      : "-"
                              }
                            />
                          )}
                          {visibleColumns.playbook_flag && (
                            <TruncatedCell
                              content={
                                detail.playbook_flag === "Y" ? "Yes" : "No"
                              }
                            />
                          )}
                          {visibleColumns.playbook_id && (
                            <TruncatedCell
                              content={detail.playbook_id || "-"}
                            />
                          )}
                          {visibleColumns.action_in_playbook && (
                            <TruncatedCell
                              content={detail.action_in_playbook || "-"}
                              maxWidth="400px"
                              columnKey="Action in Playbook"
                            />
                          )}
                          {visibleColumns.occurrence && (
                            <TruncatedCell content={detail.occurrence || "1"} />
                          )}
                          {visibleColumns.process_reporting_url && (
                            <TruncatedCell
                              content={detail.process_reporting_url || "-"}
                              isUrl
                              maxWidth="450px"
                              columnKey="process_reporting_url"
                            />
                          )}
                          {visibleColumns.folder && (
                            <TruncatedCell content={detail.folder || "-"} />
                          )}
                          {visibleColumns.analysis && (
                            <TruncatedCell
                              content={detail.analysis || "-"}
                              maxWidth="500px"
                              columnKey="Analysis"
                            />
                          )}
                          {visibleColumns.scheduled_type && (
                            <TruncatedCell
                              content={getScheduledTypeLabel(
                                detail.scheduled_type,
                              )}
                            />
                          )}
                          {visibleColumns.error_type && (
                            <TruncatedCell
                              content={getErrorTypeLabel(detail.error_type)}
                            />
                          )}
                          {visibleColumns.error_name && (
                            <TruncatedCell
                              content={detail.error_name || "-"}
                              maxWidth="400px"
                              columnKey="Error Name"
                            />
                          )}
                          {visibleColumns.end_point && (
                            <TruncatedCell content={detail.end_point || "-"} />
                          )}
                          {visibleColumns.connector && (
                            <TruncatedCell content={detail.connector || "-"} />
                          )}
                          {visibleColumns.action && (
                            <TruncatedCell
                              content={getActionLabel(detail.action)}
                            />
                          )}
                          {visibleColumns.doc_id && (
                            <TruncatedCell content={detail.doc_id || "-"} />
                          )}
                          {visibleColumns.comments && (
                            <TruncatedCell
                              content={detail.comments || "-"}
                              maxWidth="450px"
                            />
                          )}
                          {visibleColumns.active_flag && (
                            <TruncatedCell
                              content={
                                detail.active_flag === "1"
                                  ? "Active"
                                  : "Inactive"
                              }
                            />
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </ThemeProvider>
          ) : (
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              minHeight="200px"
              sx={{
                padding: "20px",
              }}
            >
              <Typography>No failure tracking details available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editField}
        onClose={() => setEditField(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: "700",
            fontSize: "16px",
            py: 1,
          }}
        >
          Edit {editField?.field}
          <IconButton
            onClick={() => setEditField(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { step: 1 } }}
            label={`New ${editField?.field}`}
            type={
              editField?.field === "reported_date" ||
              editField?.field === "received_date"
                ? "date"
                : editField?.field === "error_time"
                  ? "text"
                  : "text"
            }
            value={
              editField?.value ||
              (editField?.field === "reported_date"
                ? parentReportedDate
                : editField?.field === "received_date"
                  ? parentReceivedDate
                  : "")
            }
            onChange={(e) =>
              setEditField((prev) => ({ ...prev, value: e.target.value }))
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditField(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setLoading(true);
              try {
                // 1) save updated field (error_time or others)
                await updateSingleField(
                  editField.jed_id,
                  editField.field,
                  editField.value || null,
                );

                // 2) THIS IS THE PLACE TO ADD
                // update occurrence only when error_time changes
                if (editField.field === "error_time") {
                  const times = (editField.value || "")
                    .split(",")
                    .map((t) => t.trim())
                    .filter((t) => t.length > 0);

                  const occurrence = times.length.toString();

                  await updateSingleField(
                    editField.jed_id,
                    "occurrence",
                    occurrence,
                  );
                }

                setEditField(null);
                setFormErrors({});
                onRefresh();
              } catch (err) {
                alert("Error updating field: " + err.message);
              } finally {
                setLoading(false);
              }
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      {/* ============================
         ADD / EDIT FORM DIALOG (NEW)
      ============================ */}
      <Dialog
        open={showForm}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleDialogClose();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isEditing
            ? "Edit Failure Tracking Details"
            : "Add Failure Tracking Details"}
          <IconButton
            onClick={handleDialogClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ overflowY: "auto", maxHeight: "80vh" }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleFormSubmit}
            sx={{
              maxWidth: "1200px",
              mx: "auto",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(5, 1fr)",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <TextField
              label="Client"
              name="client_name"
              value={formData.client_name}
              onChange={handleFormChange}
              variant="outlined"
              error={!!formErrors.cl_id}
              helperText={formErrors.cl_id}
              required
              InputProps={{ readOnly: true }}
              sx={{ ...compactFieldStyles }}
            />
            <TextField
              label="Environment"
              name="env_name"
              value={formData.env_name}
              onChange={handleFormChange}
              variant="outlined"
              error={!!formErrors.env_id}
              helperText={formErrors.env_id}
              required
              InputProps={{ readOnly: true }}
              sx={{ ...compactFieldStyles }}
            />
            <TextField
              label="Application"
              name="app_name"
              value={formData.app_name}
              onChange={handleFormChange}
              variant="outlined"
              error={!!formErrors.app_id}
              helperText={formErrors.app_id}
              required
              InputProps={{ readOnly: true }}
              sx={{ ...compactFieldStyles }}
            />
            <TextField
              label="Failure Tracking ID"
              name="je_id"
              value={formData.je_id}
              onChange={handleFormChange}
              variant="outlined"
              error={!!formErrors.je_id}
              helperText={formErrors.je_id}
              required
              InputProps={{ readOnly: true }}
              sx={{ ...compactFieldStyles }}
            />
            <TextField
              label="Job ID"
              name="job_id"
              value={formData.job_id}
              variant="outlined"
              required
              error={!!formErrors.job_id}
              helperText={formErrors.job_id}
              InputProps={{ readOnly: true }}
              sx={{ ...compactFieldStyles }}
            />
            <TextField
              label="Client Job ID"
              name="client_job_id"
              value={formData.client_job_id}
              onChange={handleFormChange}
              variant="outlined"
              error={!!formErrors.client_job_id}
              helperText={
                formErrors.client_job_id || formData.app_name !== "Boomi"
              }
              required={formData.app_name === "Boomi"}
              sx={{ ...compactFieldStyles }}
            />
            <TextField
              type="date"
              label="Reported Date"
              name="reported_date"
              value={formData.reported_date}
              onChange={handleFormChange}
              variant="outlined"
              error={!!formErrors.reported_date}
              helperText={formErrors.reported_date}
              InputLabelProps={{ shrink: true }}
              required
              sx={{ ...compactFieldStyles }}
            />
            <TextField
              type="date"
              label="Received Date"
              name="received_date"
              value={formData.received_date}
              onChange={handleFormChange}
              variant="outlined"
              error={!!formErrors.received_date}
              helperText={formErrors.received_date}
              InputLabelProps={{ shrink: true }}
              required
              sx={{ ...compactFieldStyles }}
            />
            <Autocomplete
              options={playbookIdOptionsDynamic}
              getOptionLabel={(option) => option.label}
              value={
                formData.playbook_id
                  ? playbookIdOptionsDynamic.find(
                      (option) => option.value === formData.playbook_id,
                    ) || {
                      value: formData.playbook_id,
                      label: formData.playbook_id,
                    }
                  : { value: "", label: "Select a Playbook" }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  playbook_id: newValue ? newValue.value : "",
                }));
              }}
              disabled={formData.app_name === "GARPAC"}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Playbook ID"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />
            <Autocomplete
              options={[
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ]}
              getOptionLabel={(option) => option.label}
              value={
                [
                  { value: "Y", label: "Yes" },
                  { value: "N", label: "No" },
                ].find((option) => option.value === formData.playbook_flag) || {
                  value: "",
                  label: "Select Playbook Flag",
                }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  playbook_flag: newValue ? newValue.value : "",
                }));
              }}
              disabled={formData.app_name === "GARPAC"}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Playbook Flag"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />

            <Autocomplete
              options={endpointOptionsDynamic}
              getOptionLabel={(option) => option.label}
              value={
                formData.end_point
                  ? endpointOptionsDynamic.find(
                      (option) => option.value === formData.end_point,
                    ) || {
                      value: formData.end_point,
                      label: formData.end_point,
                    }
                  : { value: "", label: "Select an Endpoint" }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  end_point: newValue ? newValue.value : "",
                }));
              }}
              disabled={formData.app_name === "GARPAC"}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Endpoint"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />
            <TextField
              label="Occurrence"
              name="occurrence"
              value={formData.occurrence}
              onChange={handleFormChange}
              variant="outlined"
              type="number"
            />

            <Autocomplete
              options={actionOptions}
              getOptionLabel={(option) => option.label}
              value={
                actionOptions.find(
                  (option) => option.value === formData.action,
                ) || { value: "", label: "Select an Action" }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  action: newValue ? newValue.value : "",
                }));
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Action Taken"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                  error={!!formErrors.action}
                  helperText={formErrors.action}
                />
              )}
            />
            <Autocomplete
              options={folderOptions}
              getOptionLabel={(option) => option.label}
              value={
                folderOptions.find((opt) => opt.value === formData.folder) ||
                (formData.folder
                  ? { value: formData.folder, label: formData.folder }
                  : null)
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  folder: newValue ? newValue.value : "",
                }));
              }}
              disabled={formData.app_name === "GARPAC" || folderLoading}
              loading={folderLoading}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Folder"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {folderLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              options={[
                { value: "L", label: "Low" },
                { value: "M", label: "Medium" },
                { value: "H", label: "High" },
              ]}
              getOptionLabel={(option) => option.label}
              value={
                [
                  { value: "L", label: "Low" },
                  { value: "M", label: "Medium" },
                  { value: "H", label: "High" },
                ].find((option) => option.value === formData.criticality) || {
                  value: "",
                  label: "Select Criticality",
                }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  criticality: newValue ? newValue.value : "",
                }));
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Criticality"
                  variant="outlined"
                  error={!!formErrors.criticality}
                  helperText={formErrors.criticality}
                  required
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />
            <TextField
              label="Scheduled Type"
              name="scheduled_type"
              value={formData.scheduled_type}
              variant="outlined"
              required
              error={!!formErrors.scheduled_type}
              helperText={formErrors.scheduled_type}
              InputProps={{ readOnly: true }}
              sx={{ ...compactFieldStyles }}
            />

            <Autocomplete
              options={errorTypeOptions}
              getOptionLabel={(option) => option.label}
              value={
                errorTypeOptions.find(
                  (option) => option.value === formData.error_type,
                ) || { value: "", label: "Select Error Type" }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  error_type: newValue ? newValue.value : "",
                }));
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Error Type"
                  variant="outlined"
                  error={!!formErrors.error_type}
                  helperText={formErrors.error_type}
                  required
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />
            <Autocomplete
              options={[
                { value: "CET", label: "CET" },
                { value: "PST", label: "PST" },
              ]}
              getOptionLabel={(option) => option.label}
              value={
                [
                  { value: "CET", label: "CET" },
                  { value: "PST", label: "PST" },
                ].find((option) => option.value === formData.time_zone) || {
                  value: "",
                  label: "Select Time Zone",
                }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  time_zone: newValue ? newValue.value : "",
                }));
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Time Zone"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />
            <Autocomplete
              options={[
                { value: "1", label: "Active" },
                { value: "0", label: "Inactive" },
              ]}
              getOptionLabel={(option) => option.label}
              value={
                [
                  { value: "1", label: "Active" },
                  { value: "0", label: "Inactive" },
                ].find((option) => option.value === formData.active_flag) || {
                  value: "1",
                  label: "Active",
                }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  active_flag: newValue ? newValue.value : "1",
                }));
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Active Flag"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />
            <Autocomplete
              options={connectorOptionsDynamic}
              getOptionLabel={(option) => option.label}
              value={
                formData.connector
                  ? connectorOptionsDynamic.find(
                      (option) => option.value === formData.connector,
                    ) || {
                      value: formData.connector,
                      label: formData.connector,
                    }
                  : { value: "", label: "Select a Connector" }
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  connector: newValue ? newValue.value : "",
                }));
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "0.875rem",
                    minHeight: "32px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Connector"
                  variant="outlined"
                  sx={{ ...compactFieldStyles }}
                />
              )}
            />
            <TextField
              select
              fullWidth
              label="Action in Playbook"
              name="action_in_playbook"
              value={formData.action_in_playbook || ""}
              onChange={handleFormChange}
              margin="normal"
              // sx={{ ...compactMultilineStyles, width: "100%" }}
              SelectProps={dropdownMenuProps}
            >
              {actionInPlaybookOptions.map((option) => (
                <MenuItem key={option} value={option} sx={menuItemStyles}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                type="text"
                label="Error Time"
                name="error_time"
                value={formData.error_time}
                onChange={handleFormChange}
                variant="outlined"
                error={!!formErrors.error_time}
                helperText={formErrors.error_time}
                required
                multiline
                rows={4}
                sx={{ ...compactMultilineStyles, width: "100%" }}
              />
              <TextField
                label="Error Message"
                name="error_message"
                value={formData.error_message}
                onChange={handleFormChange}
                variant="outlined"
                error={!!formErrors.error_message}
                helperText={formErrors.error_message}
                multiline
                rows={4}
                required
                sx={{ ...compactMultilineStyles, width: "100%" }}
              />
            </Box>
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                label="Error Name"
                name="error_name"
                value={formData.error_name}
                onChange={handleFormChange}
                variant="outlined"
                multiline
                rows={3}
                error={!!formErrors.error_name}
                helperText={formErrors.error_name}
                sx={{ ...compactMultilineStyles, width: "100%" }}
              />

              <TextField
                label="Analysis"
                name="analysis"
                value={formData.analysis}
                onChange={handleFormChange}
                variant="outlined"
                error={!!formErrors.analysis}
                helperText={formErrors.analysis}
                multiline
                rows={4}
                sx={{ ...compactMultilineStyles, width: "100%" }}
              />
            </Box>
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                label="Process Reporting URL"
                name="process_reporting_url"
                value={formData.process_reporting_url}
                onChange={handleFormChange}
                variant="outlined"
                multiline
                rows={4}
                disabled={formData.app_name === "GARPAC"}
                sx={{ ...compactMultilineStyles, width: "100%" }}
              />

              {/* <TextField
                label="Doc ID"
                name="doc_id"
                value={formData.doc_id}
                onChange={handleFormChange}
                variant="outlined"
                multiline
                rows={3}
                sx={{ ...compactMultilineStyles, width: "100%" }}
              /> */}
              <TextField
                label="Comments"
                name="comments"
                value={formData.comments}
                onChange={handleFormChange}
                variant="outlined"
                multiline
                rows={3}
                sx={{ ...compactMultilineStyles, width: "100%" }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            disabled={isEditing && !hasFormChanged()}
            sx={{ bgcolor: "#2196f3" }}
          >
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default FailureTrackingPopup;
